import { useSceneSeatPermission } from "@components/table/members/assign-seats/use-scene-seat-permission";
import { useSeatsAvailable } from "@components/table/members/assign-seats/use-seats-available";
import { XgSeatCheckbox } from "@components/table/members/assign-seats/xg-seat-checkbox";
import { hasSeatShortage } from "@components/table/members/bulk-actions/bulk-seats-helper";
import { SeatShortageAlert } from "@components/table/members/bulk-actions/seat-shortage-alert";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { Chip } from "@flat-ui";
import { useAppSelector } from "@store/store-helper";
import { isBulkActionProcessingSelector, updatingItemsSelector } from "@store/table/table-selector";
import { MEMBER_SEAT_LABELS } from "@utils/member-seats-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useRef } from "react";

/** The note next to a member in the dialog that is given a Sphere XG seat along with the role */
export const NEEDS_XG_SEAT_NOTE = "Needs seat";

/** Where the seats are given from, as tracked */
const LOCATION = "bulk-change-role";
const XG_SEAT_LABEL = MEMBER_SEAT_LABELS.xgSeat;

/** Why a member failed, when they were given the seat but their role could not be changed */
export const XG_SEAT_KEPT_MESSAGE = `Was given a ${XG_SEAT_LABEL} seat, which they keep, but the role could not be changed:`;
/**
 * Applies the Sphere XG seats to changing the role of several members at once, the same way
 * useChangeWorkspaceRole does for a single one: the members that lack the seat the new role needs
 * are given one first. Since every one of them needs a free seat, the change can only be confirmed
 * once the selection fits the free seats, which the user gets to by removing members from it.
 *
 * Members that are not allowed to change, e.g. the current user, are left out of the count.
 */
export function useBulkRoleXgSeats({
  selectedEntities,
  isXgSeatRequired
}) {
  const {
    setXgSeatPermission,
    refreshSeatUsage
  } = useSceneSeatPermission();
  const {
    trackEvent
  } = useTrackEvent();
  const seatsAvailable = useSeatsAvailable().xgSeat;
  const updatingItems = useAppSelector(updatingItemsSelector);
  const isProcessing = useAppSelector(isBulkActionProcessingSelector);
  const notAllowedIds = new Set(updatingItems.filter(item => item.status === "not-allowed").map(item_0 => item_0.id));
  const idsNeedingSeat = new Set(selectedEntities.filter(member => !notAllowedIds.has(member.identity) && isXgSeatRequired(member)).map(member_0 => member_0.identity));
  const seatsNeeded = idsNeedingSeat.size;
  const seatShortage = {
    numberOfChangingMembers: seatsNeeded,
    seatsAvailable
  };
  const isShortOfSeats = hasSeatShortage(seatShortage);
  // The counts change while the seats are given, so they are only shown before confirming
  const shouldShowSeats = seatsNeeded > 0 && !isProcessing;

  // How many seats were given in the current run. Once it ends, they are tracked together, as the
  // other places that give several members a seat do, and the seats in use are fetched again,
  // since they only change on the backend, which keeps the free seats right for a retry.
  const seatsGivenRef = useRef(0);
  useEffect(() => {
    if (isProcessing || !seatsGivenRef.current) {
      return;
    }
    trackEvent({
      name: WorkspaceEvents.assignXgSeat,
      props: {
        location: LOCATION,
        numberOfMembers: seatsGivenRef.current
      }
    });
    seatsGivenRef.current = 0;
    refreshSeatUsage();
  }, [isProcessing, refreshSeatUsage, trackEvent]);
  return {
    getItemNote,
    // Above the list, as in the bulk action for the seats, so that it is seen before the members
    alert: shouldShowSeats ? <SeatShortageAlert {...seatShortage} /> : null,
    // The seat card the members are given their seat with, as in the Manage seats dialog and the
    // invites, which tells how many of the remaining seats they use
    footer: shouldShowSeats ? <XgSeatCheckbox numberOfMembers={seatsNeeded} seatsAvailable={seatsAvailable} description={`Will be assigned to the members marked "${NEEDS_XG_SEAT_NOTE}" before their role changes`} /> : null,
    hasSeatShortage: isShortOfSeats,
    changeRoleWithXgSeat
  };
  function getItemNote(itemId) {
    if (!idsNeedingSeat.has(itemId)) {
      return undefined;
    }
    return <Chip label={NEEDS_XG_SEAT_NOTE} color={isShortOfSeats ? "error" : "warning"} tooltip={`Has no ${XG_SEAT_LABEL} seat yet, and is given one along with the role`} />;
  }
  async function changeRoleWithXgSeat(member_1, changeRole) {
    if (!idsNeedingSeat.has(member_1.identity)) {
      await changeRole();
      return;
    }
    await setXgSeatPermission(member_1);
    seatsGivenRef.current += 1;
    try {
      await changeRole();
    } catch (error) {
      // A seat cannot be taken away again, so the member keeps it, which the user has to know.
      // Trying again only changes the role, since the member holds the seat by then.
      throw new Error(`${XG_SEAT_KEPT_MESSAGE} ${getErrorDisplayMarkup(error)}`, {
        cause: error
      });
    }
  }
}